import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import ChatPreview from "./ChatPreview";
import { waLink, WA_MSG } from "./wa";

const scrollTo = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Wrap = styled.section`
  position: relative;
  padding: 3.5rem 0 4.5rem;
  overflow: hidden;

  @media (min-width: 900px) {
    padding: 5rem 0 6rem;
  }
`;

const Wash = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(64% 56% at 82% -4%, rgba(var(--accent-rgb), 0.10), transparent 60%),
    radial-gradient(56% 50% at -2% 26%, rgba(17, 20, 15, 0.03), transparent 60%);
  pointer-events: none;
  z-index: 0;
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 1.5rem;

  @media (min-width: 768px) { padding: 0 2rem; }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  align-items: start;

  @media (min-width: 960px) {
    grid-template-columns: 1.05fr 0.95fr;
    gap: 4.5rem;
  }
`;

const Headline = styled(motion.h1)`
  font-family: var(--sans);
  font-weight: 700;
  font-size: clamp(2.5rem, 5.4vw, 4.6rem);
  line-height: 0.98;
  letter-spacing: -0.036em;
  color: var(--ink);
  margin: 0;
  max-width: 13ch;

  .accent {
    font-family: var(--serif);
    font-weight: 400;
    font-style: italic;
    letter-spacing: -0.012em;
    color: var(--accent);
  }
`;

const Sub = styled(motion.p)`
  font-family: var(--sans);
  font-size: clamp(1.02rem, 1.4vw, 1.15rem);
  line-height: 1.55;
  color: var(--ink-2);
  max-width: 50ch;
  margin: 1.5rem 0 0;
  font-weight: 400;
`;

const Ctas = styled(motion.div)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.2rem;
  margin: 2rem 0 0;
`;

const Primary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-family: var(--sans);
  font-size: 0.95rem;
  font-weight: 500;
  background: var(--ink);
  color: #fff;
  text-decoration: none;
  padding: 14px 22px;
  border-radius: 999px;
  letter-spacing: -0.005em;
  transition: background 200ms ease, transform 220ms cubic-bezier(0.2, 0, 0, 1);
  white-space: nowrap;

  &:hover {
    background: var(--accent);
    transform: translateY(-1px);
  }

  svg { width: 13px; height: 13px; }
`;

const Secondary = styled.a`
  font-family: var(--sans);
  font-size: 0.93rem;
  font-weight: 500;
  color: var(--ink-2);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: color 200ms ease;

  &:hover { color: var(--accent); }

  svg {
    width: 13px;
    height: 13px;
    transition: transform 220ms ease;
  }

  &:hover svg { transform: translateX(2px); }
`;

const Right = styled(motion.div)`
  display: flex;
  justify-content: center;
  margin-top: -15px;

  @media (min-width: 960px) {
    justify-content: flex-end;
  }
`;

/* Hero chat — Valentina + Pelusa scenario, loops with typing dots
   before every message on the right side. */
const HERO_MESSAGES = [
  {
    from: "out",
    time: "10:42",
    read: true,
    body: <>Hola Valentina, te recordamos que es hora de reponer la comida de Pelusa 🐱. ¿Gestionamos tu nueva compra?</>,
  },
  {
    from: "in",
    time: "10:43",
    body: <>Hola! sí porfa, lo mismo de siempre… ¿tienen algún snack en oferta?</>,
  },
  {
    from: "out",
    time: "10:43",
    read: true,
    body: (
      <>
        ¡Sí! tenemos estos:<br />
        <br />
        · Cat Snack — galletas salmón<br />
        · Wampy Creamy Treat — pollo<br />
        <br />
        ¿Te gustaría alguno?
      </>
    ),
  },
  {
    from: "in",
    time: "10:44",
    body: <>Incluye las galletas de salmón por favor 🙏</>,
  },
  {
    from: "out",
    time: "10:44",
    read: true,
    body: <>¡Listo! te envío el link de tu carrito… 🛒</>,
  },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function HeroChat() {
  return (
    <Wrap>
      <Wash />
      <Inner>
        <TwoCol>
          <div>
            <Headline {...fade(0.05)}>
              Agentes de IA que le <span className="accent">vuelven a vender</span> a tus clientes.
            </Headline>

            <Sub {...fade(0.18)}>
              Tu equipo de IA que genera conversaciones oportunas con tus
              clientes para que vuelvan a comprarte.
            </Sub>

            <Ctas {...fade(0.28)}>
              <Primary href="#como-funciona" onClick={scrollTo("como-funciona")}>
                Cómo funciona
                <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Primary>
              <Secondary href={waLink(WA_MSG.demo)} target="_blank" rel="noreferrer">
                Agendar demo
                <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Secondary>
            </Ctas>
          </div>

          <Right
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            <ChatPreview
              contact={{ name: "Valentina · Pelusa 🐱", initial: "V", status: "en línea" }}
              messages={HERO_MESSAGES}
              loop
              pace={1}
            />
          </Right>
        </TwoCol>
      </Inner>
    </Wrap>
  );
}
